import axios from 'axios'
import { isAzureAuthEnabled } from '@/auth/authMode'
import { msalInstance } from '@/auth/msalInstance'
import { apiScopes } from '@/auth/msalConfig'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: { 'Content-Type': 'application/json' },
})

export async function getAuthorizationHeaderValue(): Promise<string | null> {
  // Check for local auth token first
  const localToken = localStorage.getItem('cmos_token')
  if (localToken) {
    return `Bearer ${localToken}`
  }

  // Fall back to MSAL (Azure AD)
  if (!isAzureAuthEnabled || !msalInstance) {
    return null
  }

  const active = msalInstance.getActiveAccount()
  const account = active ?? msalInstance.getAllAccounts()[0] ?? null
  if (account) {
    if (!active) {
      msalInstance.setActiveAccount(account)
    }
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiScopes,
        account,
      })
      return `Bearer ${response.accessToken}`
    } catch {
      // Silent acquisition failed. This used to call acquireTokenRedirect(),
      // navigating the whole page to Microsoft's login — fine for one call,
      // but every component's API request hits this same path, so a stale
      // cached MSAL account plus a broken/unconsented app registration fired
      // a redirect on every single one of them, nearly simultaneously,
      // bouncing the page between the app and login.microsoftonline.com in
      // an uncontrolled loop. Microsoft sign-in isn't even a path users can
      // choose today (LoginPage's SHOW_MICROSOFT_LOGIN_UI is false) — let
      // this fail quietly instead of hijacking navigation. The explicit,
      // user-initiated handleMicrosoftLogin() button flow is unaffected.
      return null
    }
  }
  return null
}

// Acquire token — local JWT takes priority, then MSAL
api.interceptors.request.use(async (config) => {
  const authorization = await getAuthorizationHeaderValue()
  if (authorization) {
    config.headers.Authorization = authorization
  }
  // A file upload is multipart, and the boundary is generated by the browser
  // when it serialises the FormData. The instance-wide default above declared
  // every request `application/json`, which axios leaves in place for a
  // FormData body — so the request went out claiming JSON with no boundary at
  // all and FastAPI parsed no fields from it: every upload in the app failed
  // with 422 "file: Field required". Clearing the header lets the browser set
  // `multipart/form-data; boundary=…` itself.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401) {
      // Check if using local auth — clear and reload
      const localToken = localStorage.getItem('cmos_token')
      if (localToken) {
        localStorage.removeItem('cmos_token')
        localStorage.removeItem('cmos_user')
        window.location.reload()
        return Promise.reject(err)
      }

      // No local token and the request was unauthorized — same reasoning as
      // getAuthorizationHeaderValue(): don't auto-redirect to Microsoft from
      // a background response handler. Let the caller (and ProtectedRoute)
      // handle the plain 401.
    }
    return Promise.reject(err)
  }
)

export default api
